import { useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import UpgradeModal from './UpgradeModal';

interface Carta {
  id: string;
  mensagem: string;
  prazo: string;
  data_criacao: string;
  data_entrega: string;
  status_pagamento: string;
  email_enviado: boolean;
  plano?: string;
  status?: string;
  conteudo?: string;
  situacao_atual?: string;
  desejo_futuro?: string;
  prazo_anos?: number;
  lembretes_ativados?: boolean;
}

interface CartaCardProps {
  carta: Carta;
  onRefresh: () => void;
}

const CartaCard = ({ carta, onRefresh }: CartaCardProps) => {
  const [showFullMessage, setShowFullMessage] = useState(false);
  const [showCarta, setShowCarta] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [togglingLembrete, setTogglingLembrete] = useState(false);

  const isGratuito = carta.plano === 'gratuito' || carta.status === 'gratuito';
  const isPremium = !isGratuito;
  const lembretesAtivados = carta.lembretes_ativados !== false;

  const calcularProgresso = () => {
    const agora = new Date().getTime();
    const criacao = new Date(carta.data_criacao).getTime();
    const entrega = new Date(carta.data_entrega).getTime();
    const total = entrega - criacao;
    const decorrido = agora - criacao;
    const progresso = Math.min(Math.max((decorrido / total) * 100, 0), 100);
    return Math.round(progresso);
  };

  const calcularDiasRestantes = () => {
    const agora = new Date();
    const entrega = new Date(carta.data_entrega);
    const diff = entrega.getTime() - agora.getTime();
    const dias = Math.ceil(diff / (1000 * 60 * 60 * 24));
    return dias > 0 ? dias : 0;
  };

  const prazoExpirou = () => {
    const agora = new Date();
    const entrega = new Date(carta.data_entrega);
    return agora >= entrega;
  };

  const formatarData = (data: string) => {
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusInfo = () => {
    if (isGratuito) {
      if (prazoExpirou()) {
        return {
          icon: 'ri-mail-open-line',
          text: 'Dispon\u00edvel para leitura',
          color: 'text-green-400',
          bg: 'bg-green-500/10',
          border: 'border-green-500/30'
        };
      }
      return {
        icon: 'ri-lock-line',
        text: 'Gratuito \u2022 Aguardando prazo',
        color: 'text-gold-400',
        bg: 'bg-gold-500/10',
        border: 'border-gold-500/30'
      };
    }

    if (carta.status_pagamento === 'pendente') {
      return {
        icon: 'ri-time-line',
        text: 'Pagamento Pendente',
        color: 'text-yellow-400',
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/30'
      };
    }

    if (carta.email_enviado) {
      return {
        icon: 'ri-mail-check-line',
        text: 'Premium \u2022 Entregue',
        color: 'text-green-400',
        bg: 'bg-green-500/10',
        border: 'border-green-500/30'
      };
    }

    return {
      icon: 'ri-vip-crown-2-line',
      text: 'Premium \u2022 Em Andamento',
      color: 'text-gold-400',
      bg: 'bg-gold-500/10',
      border: 'border-gold-500/30'
    };
  };

  const toggleLembrete = async () => {
    setTogglingLembrete(true);
    try {
      await supabase
        .from('cartas')
        .update({ lembretes_ativados: !lembretesAtivados })
        .eq('id', carta.id);
      onRefresh();
    } catch (err) {
      console.error('Erro ao atualizar lembrete:', err);
    } finally {
      setTogglingLembrete(false);
    }
  };

  const statusInfo = getStatusInfo();
  const progresso = calcularProgresso();
  const diasRestantes = calcularDiasRestantes();
  const podeVerCarta = isGratuito && prazoExpirou();

  const mensagemExibida = carta.conteudo || carta.mensagem || '';
  const mensagemTruncada = mensagemExibida.length > 150
    ? mensagemExibida.substring(0, 150) + '...'
    : mensagemExibida;

  return (
    <>
      <div className="bg-luxury-dark/60 backdrop-blur-xl border border-gold-500/20 rounded-2xl p-6 hover:border-gold-500/40 transition-all duration-500 hover:shadow-[0_0_30px_rgba(201,168,76,0.1)] group">
        {/* Header do card */}
        <div className="flex items-start justify-between mb-4">
          <div className={`flex items-center gap-2 ${statusInfo.bg} ${statusInfo.border} border rounded-lg px-3 py-1.5`}>
            <i className={`${statusInfo.icon} ${statusInfo.color} text-lg`}></i>
            <span className={`font-['Montserrat'] text-base ${statusInfo.color} tracking-wide`}>
              {statusInfo.text}
            </span>
          </div>

          <div className="text-right">
            <p className="font-['Montserrat'] text-base text-white/40 uppercase tracking-wider">
              Prazo
            </p>
            <p className="font-['Playfair_Display'] text-2xl font-semibold text-gold-400">
              {carta.prazo_anos || carta.prazo} {(carta.prazo_anos || parseInt(carta.prazo || '0')) === 1 ? 'ano' : 'anos'}
            </p>
          </div>
        </div>

        {/* Badge do plano + Lembrete */}
        <div className="flex items-center gap-2 mb-4">
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${
            isGratuito ? 'bg-white/5 border border-white/10' : 'bg-gold-500/10 border border-gold-500/20'
          }`}>
            <i className={`text-base ${isGratuito ? 'ri-gift-line text-white/50' : 'ri-vip-crown-2-fill text-gold-400'}`}></i>
            <span className={`font-['Montserrat'] text-base tracking-wide ${isGratuito ? 'text-white/50' : 'text-gold-400'}`}>
              {isGratuito ? 'Gratuito' : 'Premium'}
            </span>
          </div>

          {!prazoExpirou() && (
            <button
              onClick={toggleLembrete}
              disabled={togglingLembrete}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border transition-all duration-300 cursor-pointer ${
                lembretesAtivados
                  ? 'bg-gold-500/10 border-gold-500/20 hover:bg-gold-500/20'
                  : 'bg-white/[0.02] border-white/10 hover:bg-white/5'
              } ${togglingLembrete ? 'opacity-50' : ''}`}
              title={lembretesAtivados ? 'Desativar lembretes' : 'Ativar lembretes'}
            >
              <i className={`text-base ${lembretesAtivados ? 'ri-notification-3-fill text-gold-400' : 'ri-notification-off-line text-white/30'}`}></i>
              <span className={`font-['Montserrat'] text-base tracking-wide ${lembretesAtivados ? 'text-gold-400' : 'text-white/30'}`}>
                {togglingLembrete ? '...' : lembretesAtivados ? 'Lembretes' : 'Sem lembretes'}
              </span>
            </button>
          )}
        </div>

        {/* Barra de progresso */}
        {(isGratuito || (carta.status_pagamento !== 'pendente' && !carta.email_enviado)) && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-['Montserrat'] text-base text-white/60 tracking-wide">
                {isGratuito ? 'Tempo até liberação' : 'Progresso da jornada'}
              </span>
              <span className="font-['Montserrat'] text-base font-semibold text-gold-400">
                {progresso}%
              </span>
            </div>
            <div className="h-2 bg-black/40 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ${
                  podeVerCarta
                    ? 'bg-gradient-to-r from-green-400 to-green-500'
                    : 'bg-gradient-to-r from-gold-500 to-gold-600'
                }`}
                style={{ width: `${progresso}%` }}
              ></div>
            </div>
            <p className="font-['Montserrat'] text-base text-white/40 mt-2 tracking-wide">
              <i className="ri-calendar-line mr-1"></i>
              {diasRestantes > 0 ? `${diasRestantes} dias restantes` : 'Prazo concluído!'}
            </p>
          </div>
        )}

        {/* Info especial para gratuito */}
        {isGratuito && !podeVerCarta && (
          <div className="mb-6 bg-white/[0.03] rounded-lg p-4 border border-gold-500/10">
            <div className="flex items-start gap-3">
              <i className="ri-lock-line text-gold-400/60 mt-0.5 text-xl"></i>
              <div>
                <p className="font-['Montserrat'] text-base text-white/50 leading-relaxed">
                  Sua carta está guardada com segurança. Ela ficará disponível para leitura após <strong className="text-gold-400">{formatarData(carta.data_entrega)}</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Botão de upgrade para Premium (apenas gratuito) */}
        {isGratuito && (
          <button
            onClick={() => setShowUpgrade(true)}
            className="w-full mb-4 bg-gradient-to-r from-gold-500/10 to-gold-700/10 border border-gold-500/30 text-gold-400 font-['Montserrat'] font-semibold text-base py-3 rounded-lg hover:bg-gold-500/20 hover:border-gold-500/50 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer group/upgrade"
          >
            <i className="ri-vip-crown-2-fill text-lg group-hover/upgrade:scale-110 transition-transform"></i>
            Fazer upgrade para Premium
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        )}

        {/* Botão para ver carta (gratuito com prazo expirado) */}
        {podeVerCarta && (
          <button
            onClick={() => setShowCarta(true)}
            className="w-full mb-6 bg-gradient-to-r from-gold-500 to-gold-600 text-luxury-black font-['Montserrat'] font-semibold text-lg py-3 rounded-lg hover:shadow-[0_0_20px_rgba(201,168,76,0.3)] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-open-line"></i>
            Ler minha carta
          </button>
        )}

        {/* Datas */}
        <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-gold-500/10">
          <div>
            <p className="font-['Montserrat'] text-base text-white/40 uppercase tracking-wider mb-1">
              Criada em
            </p>
            <p className="font-['Montserrat'] text-base text-white tracking-wide">
              {formatarData(carta.data_criacao)}
            </p>
          </div>
          <div>
            <p className="font-['Montserrat'] text-base text-white/40 uppercase tracking-wider mb-1">
              {isGratuito ? 'Disponível em' : 'Entrega em'}
            </p>
            <p className="font-['Montserrat'] text-base text-gold-400 tracking-wide">
              {formatarData(carta.data_entrega)}
            </p>
          </div>
        </div>

        {/* Preview da mensagem */}
        <div className="mb-4">
          <p className="font-['Montserrat'] text-base text-white/40 uppercase tracking-wider mb-2">
            {isGratuito && !podeVerCarta ? 'Prévia da mensagem' : 'Sua mensagem'}
          </p>
          {isGratuito && !podeVerCarta ? (
            <div className="relative">
              <p className="font-['Montserrat'] text-lg text-white/30 leading-relaxed tracking-wide blur-sm select-none">
                {mensagemTruncada || 'Sua mensagem está guardada com segurança...'}
              </p>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-luxury-dark/80 backdrop-blur-sm rounded-lg px-4 py-2 border border-gold-500/20">
                  <p className="font-['Montserrat'] text-base text-gold-400 flex items-center gap-2">
                    <i className="ri-lock-line"></i>
                    Disponível após o prazo
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="font-['Montserrat'] text-lg text-white/80 leading-relaxed tracking-wide">
                {showFullMessage ? mensagemExibida : mensagemTruncada}
              </p>
              {mensagemExibida.length > 150 && (
                <button
                  onClick={() => setShowFullMessage(!showFullMessage)}
                  className="font-['Montserrat'] text-base text-gold-400 hover:text-gold-300 mt-2 transition-colors cursor-pointer tracking-wide"
                >
                  {showFullMessage ? 'Ver menos' : 'Ver mais'}
                </button>
              )}
            </>
          )}
        </div>

        {/* Ícone decorativo */}
        <div className="flex items-center justify-center pt-4 border-t border-gold-500/10">
          <div className="w-12 h-12 rounded-full bg-gold-500/10 border border-gold-500/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
            <i className={`${isGratuito ? 'ri-gift-line' : 'ri-mail-send-line'} text-2xl text-gold-500`}></i>
          </div>
        </div>
      </div>

      {/* Modal para ler carta completa */}
      {showCarta && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-6">
          <div className="bg-luxury-dark border border-gold-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-8 lg:p-12">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center">
                    <i className="ri-mail-open-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-['Playfair_Display'] text-3xl font-semibold text-white">Sua Carta do Futuro</h3>
                    <p className="font-['Montserrat'] text-base text-white/40">Escrita em {formatarData(carta.data_criacao)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCarta(false)}
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-white/60 text-2xl"></i>
                </button>
              </div>

              <div className="w-full h-px bg-gradient-to-r from-transparent via-gold-500/30 to-transparent mb-8"></div>

              {carta.situacao_atual && (
                <div className="mb-6">
                  <p className="font-['Montserrat'] text-base text-gold-400 uppercase tracking-wider mb-2">Onde eu estava</p>
                  <p className="font-['Montserrat'] text-lg text-white/70 leading-relaxed">{carta.situacao_atual}</p>
                </div>
              )}

              {carta.desejo_futuro && (
                <div className="mb-6">
                  <p className="font-['Montserrat'] text-base text-gold-400 uppercase tracking-wider mb-2">O que eu desejava</p>
                  <p className="font-['Montserrat'] text-lg text-white/70 leading-relaxed">{carta.desejo_futuro}</p>
                </div>
              )}

              <div className="mb-8">
                <p className="font-['Montserrat'] text-base text-gold-400 uppercase tracking-wider mb-2">Minha mensagem</p>
                <div className="bg-white/[0.03] rounded-xl p-6 border border-gold-500/10">
                  <p className="font-['Cormorant_Garamond'] text-2xl text-white/80 leading-relaxed italic">
                    {mensagemExibida}
                  </p>
                </div>
              </div>

              <div className="bg-gold-500/10 border border-gold-500/20 rounded-xl p-6 text-center">
                <i className="ri-sparkling-2-line text-3xl text-gold-400 mb-2 block"></i>
                <p className="font-['Montserrat'] text-lg text-white/60">
                  Esta carta foi escrita há <strong className="text-gold-400">{carta.prazo_anos || carta.prazo} {(carta.prazo_anos || parseInt(carta.prazo || '0')) === 1 ? 'ano' : 'anos'}</strong>. Reflita sobre sua jornada.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de upgrade */}
      {showUpgrade && (
        <UpgradeModal
          cartaId={carta.id}
          prazoAnos={carta.prazo_anos || parseInt(carta.prazo || '1')}
          onClose={() => setShowUpgrade(false)}
          onUpgradeSuccess={() => {
            setShowUpgrade(false);
            onRefresh();
          }}
        />
      )}
    </>
  );
};

export default CartaCard;
